// Scrap Entry — write off damaged / obsolete stock with a reason. Posts to
// /api/stock/scrap which creates a Scrap txn and deducts the source lot.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const REASONS = ["Damaged", "Expired", "Contaminated", "QC Rejected", "Obsolete", "Other"];

export default function ScrapEntry() {
  const [rms, setRms] = useState([]);
  const [lots, setLots] = useState([]);
  const [rmId, setRmId] = useState("");
  const [lotId, setLotId] = useState("");
  const [qty, setQty] = useState(0);
  const [reason, setReason] = useState("Damaged");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Mar 2026 · Universal Deactivation Filter — hide inactive RMs from
  // new-transaction pickers. Historical rows in the Stock Register are
  // unaffected (they read directly from inventory_lots).
  useEffect(() => { api.get("/raw-materials/").then(r => setRms((r.data || []).filter(x => x.is_active !== false))); }, []);
  useEffect(() => {
    if (!rmId) { setLots([]); return; }
    api.get(`/stock/on-hand?rm_id=${rmId}`).then(r => setLots(r.data?.rows || []));
  }, [rmId]);

  const submit = async () => {
    if (!lotId || qty <= 0) { toast.error("Pick a lot + qty > 0"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/stock/scrap", { lot_id: lotId, quantity: qty, reason, remarks });
      toast.success(`Scrap posted · ₹${Number(data.value_written_off || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} written off`);
      setLastResult(data); setLotId(""); setQty(0); setRemarks("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Scrap failed"); }
    setBusy(false);
  };

  const inp = "w-full h-10 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
  return (
    <div className="space-y-4" data-testid="scrap-page">
      <div className="overline text-[10px] text-[var(--accent)]">INVENTORY</div>
      <h1 className="text-2xl font-semibold">Scrap Entry</h1>
      <p className="text-sm text-zinc-400">Write off damaged / expired / contaminated stock. Creates a permanent Scrap txn.</p>
      <div className="tactical-card p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Raw Material *</div>
            <select data-testid="scrap-rm" value={rmId} onChange={e => setRmId(e.target.value)} className={inp}>
              <option value="">— Select —</option>
              {rms.map(r => <option key={r.id} value={r.id}>{r.code} · {r.name}</option>)}
            </select>
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Lot * ({lots.length} available)</div>
            <select data-testid="scrap-lot" value={lotId} onChange={e => setLotId(e.target.value)} className={inp} disabled={!rmId}>
              <option value="">— Select —</option>
              {lots.map(l => <option key={l.lot_id} value={l.lot_id}>{l.lot_no} · qty {l.available_qty} · {l.quality_status}</option>)}
            </select>
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Quantity *</div>
            <input data-testid="scrap-qty" type="number" step="0.001" min="0" value={qty} onChange={e => setQty(parseFloat(e.target.value)||0)} className={inp} />
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
            <div>Reason</div>
            <select data-testid="scrap-reason" value={reason} onChange={e => setReason(e.target.value)} className={inp}>
              {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1 md:col-span-2">
            <div>Remarks</div>
            <input data-testid="scrap-remarks" value={remarks} onChange={e => setRemarks(e.target.value)} className={inp} placeholder="Describe the incident / batch reference" />
          </label>
        </div>
        <div className="flex justify-end pt-2">
          <button data-testid="scrap-submit" onClick={submit} disabled={busy}
            className="h-10 px-6 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-sm disabled:opacity-50">
            {busy ? "Posting…" : "Post Scrap Entry"}
          </button>
        </div>
        {lastResult && (
          <div className="mt-4 p-3 rounded-sm border border-red-500/30 bg-red-500/10 text-sm">
            Last posted: txn <span className="font-mono">{lastResult.txn_id?.slice(0, 8)}…</span> ·
            value <span className="text-red-300 font-bold">₹{Number(lastResult.value_written_off || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        )}
      </div>
    </div>
  );
}
